import { useCallback, useReducer } from 'react';
import { GlobalContext } from './Global.context';
import { globalReducer } from './Global.reducer';
import type { GlobalContextProps, GlobalProviderProps, GlobalReducerState } from './Global.types';

const STORAGE_KEY_PATIENT = 'homeopathy_patient';

function createConsultationToken() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `consultation-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getStoredPatient(): GlobalReducerState['patient'] {
  if (typeof window === 'undefined') return { email: '', name: '', age: 18, gender: 'male' };
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PATIENT);
    if (!raw) return { email: '', name: '', age: 18, gender: 'male' };
    const parsed = JSON.parse(raw) as GlobalReducerState['patient'];
    return {
      email: typeof parsed.email === 'string' ? parsed.email : '',
      name: typeof parsed.name === 'string' ? parsed.name : '',
      age: typeof parsed.age === 'number' && parsed.age >= 0 && parsed.age <= 150 ? parsed.age : 18,
      gender: typeof parsed.gender === 'string' ? parsed.gender : 'male',
      consultationToken: typeof parsed.consultationToken === 'string' ? parsed.consultationToken : undefined,
    };
  } catch {
    return { email: '', name: '', age: 18, gender: 'male' };
  }
}

const defaultPatient = getStoredPatient();

const initialState: GlobalReducerState = {
  patient: defaultPatient,
  sidebarOpen: false,
  successModal: { show: false, title: '' },
  infoModal: { show: false, title: '', message: '', html: null },
  confirmModal: { show: false, title: '', message: '', handleConfirm: () => {}, handleCancel: () => {} },
  errorModal: { show: false, title: '', message: '' },
  toast: { show: false, type: 'success', message: '' },
};

export function GlobalProvider({ children }: GlobalProviderProps) {
  const [state, dispatch] = useReducer(globalReducer, initialState);

  const setPatient = (payload: Parameters<GlobalContextProps['setPatient']>[0]) =>
    dispatch({ type: 'SET_PATIENT', payload });

  const startConsultation = useCallback(() => {
    const token = createConsultationToken();
    dispatch({ type: 'SET_PATIENT', payload: { consultationToken: token } });
    try {
      const raw = localStorage.getItem(STORAGE_KEY_PATIENT);
      const stored = raw ? JSON.parse(raw) : {};
      localStorage.setItem(STORAGE_KEY_PATIENT, JSON.stringify({ ...stored, consultationToken: token }));
    } catch { /* ignore */ }
    return token;
  }, []);

  const toggleSidebar = () => dispatch({ type: 'TOGGLE_SIDEBAR' });

  const setSuccessModal = (infoContent: { title: string }) => dispatch({ type: 'SET_SUCCESS_MODAL', payload: infoContent });

  const setConfirmModal = (confirmContent: { title: string; message: string; handleConfirm: () => void; handleCancel?: () => void }) =>
    dispatch({ type: 'SET_CONFIRM_MODAL', payload: confirmContent });

  const setErrorModal = (errorContent: { title?: string; message?: string }) => dispatch({ type: 'SET_ERROR_MODAL', payload: errorContent });

  const setInfoModal = (infoContent: Parameters<GlobalContextProps['setInfoModal']>[0]) =>
    dispatch({ type: 'SET_INFO_MODAL', payload: infoContent });

  const setToast = (toastContent: { type: 'success' | 'error'; message: string }) => dispatch({ type: 'SET_TOAST', payload: toastContent });

  const closeSuccessModal = () => {
    dispatch({ type: 'CLOSE_SUCCESS_MODAL' });
    setTimeout(() => dispatch({ type: 'CLEAN_SUCCESS_MODAL' }), 100);
  };

  const closeInfoModal = () => {
    dispatch({ type: 'CLOSE_INFO_MODAL' });
    setTimeout(() => dispatch({ type: 'CLEAN_INFO_MODAL' }), 100);
  };

  const closeConfirmModal = () => {
    dispatch({ type: 'CLOSE_CONFIRM_MODAL' });
    setTimeout(() => dispatch({ type: 'CLEAN_CONFIRM_MODAL' }), 100);
  };

  const closeErrorModal = () => {
    dispatch({ type: 'CLOSE_ERROR_MODAL' });
    setTimeout(() => dispatch({ type: 'CLEAN_ERROR_MODAL' }), 100);
  };

  const closeToast = () => {
    dispatch({ type: 'CLOSE_TOAST' });
    setTimeout(() => dispatch({ type: 'CLEAN_TOAST' }), 100);
  };

  const ctx: GlobalContextProps = {
    ...state,
    setPatient,
    startConsultation,
    toggleSidebar,
    setSuccessModal,
    setInfoModal,
    setConfirmModal,
    setErrorModal,
    closeSuccessModal,
    closeInfoModal,
    closeConfirmModal,
    closeErrorModal,
    setToast,
    closeToast,
  };

  return <GlobalContext.Provider value={ctx}>{children}</GlobalContext.Provider>;
};
